/**
 * Componente principal que carga la zona de trabajo. También contiene las funciones para generar
 * la imagen y hacer las llamadas al server para interactuar con la máquina Windows.
 **/

import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/Principal.css";
//imports firebase
import { storage, db } from "../db/FirebaseConfig.js";
import { ref, uploadBytes } from "firebase/storage";
import { collection, addDoc } from "firebase/firestore";
import { useAuth } from "../db/AuthContext.js";
//imports componentes
import MenuPrincipal from "./MenuPrincipal";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import ImagenesSeleccionadas from "./ImagenesSeleccionadas";
import Modal from "./ModalMain";

function Principal() {
  /*********************************** VARIABLES DE COMPONENTE ***********************************/
  /***********************************************************************************************/
  const [mostrarModal, setMostrarModal] = useState(false);
  const [modeloSeleccionado, setModeloSeleccionado] = useState(null);
  const [imagenSeleccionada, setImagenSeleccionada] = useState(null);
  const [isGenerada, setIsGenerada] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const [currentUser, setUser] = useState(null);
  const [imagenGenerada, setImagenGenerada] = useState(null);
  let urlImagenGenerada; //para guardar la url de la imagen generada temportalmente
  const SERVER_URL = "https://app.mixoutfit.com";

  //Detectar si es móvil y si debe ocultarse ImagenesSeleccionadas en desktop
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 850);
  const [hideImagenes, setHideImagenes] = useState(window.innerWidth <= 850);

  /***********************************************************************************************/
  /***********************************************************************************************/

  /*********************************** FUNCIONES DE COMPONENTE ***********************************/
  /***********************************************************************************************/
  //Ajusta el tamaño de interfaz si se ve desde móvil
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 850);
      setHideImagenes(window.innerWidth <= 850);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  //Se actualiza el usuario
  useEffect(() => {
    setUser(user);
  }, [user]);

  /************************************* GENERACION DE IMAGEN ***************************************/
  //Es el método que se dispara cuando se le da al botón de generar la imagen. Guarda la imagen
  //generada y la prenda utilizada, pasando por una llamda a ComfyUI. Se reciben como parámetros los
  //objetos File de la prenda y el modelo
  const handleGenerarImagen = async (filePrenda, fileModelo) => {
    //1º Se sube la imagen de la prenda a firebase y se generan los inputs para ComfyUI
    if (!filePrenda || !fileModelo || !currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true); //se cambia la imagen en display a la imagen de carga
    console.log("Subiendo prenda a firebase...");

    //Se generan las referencias a las carpeta de Firestore correspondientes
    const storagePathEspecifico = `files/${currentUser.uid}/mixoutfit/input/${filePrenda.name}`;
    const storageRefEspecifico = ref(storage, storagePathEspecifico);
    const storagePathGeneral = `files/${currentUser.uid}/${filePrenda.name}`;
    const storageRefGeneral = ref(storage, storagePathGeneral);

    //Se intentan subir las imágenes
    try {
      await uploadBytes(storageRefEspecifico, filePrenda);
      await uploadBytes(storageRefGeneral, filePrenda);

      //se guardan el nombre del archivo en Firestore, dentro de la subcolección del usuario
      //sólo se referencia el nombre, ya que es para referenciar en el Creator
      const filesCollection = collection(db, `users/${currentUser.uid}/models`);
      await addDoc(filesCollection, {
        filename: filePrenda.name,
      });
    } catch (error) {
      console.error("Error uploading file:", error);
    }

    //2º Se genera la imagen, haciendo peticiones al servicio de Cloud Run
    console.log("Generando imagen...");
    await executeWorkflow(filePrenda, fileModelo);
    //una vez completada la llamada al servicio, se quita la imagen de carga y se muestra la
    //retornada desde la máquina Windows
    setLoading(false); //se quita la imagen de carga
    setImagenSeleccionada(urlImagenGenerada); //se actualiza la imagen en display
    let fileImagenGenerada;

    //3º Se sube la imagen generada y se muestra en pantalla
    console.log("URL imagen generada: ", urlImagenGenerada);
    if (!urlImagenGenerada || !currentUser) return;

    //Se transforma la url de la imagen generada a objeto File
    await urlToFile(urlImagenGenerada).then((file) => {
      fileImagenGenerada = file;
      console.log("Imagen generada a subir: ", fileImagenGenerada);
    });

    console.log("Subiendo imagen generada a firebase...");
    const storagePathGeneradoEspecifico = `files/${currentUser.uid}/mixoutfit/output/${fileImagenGenerada.name}`;
    const storageRefGeneradoEspecifico = ref(
      storage,
      storagePathGeneradoEspecifico
    );
    const storagePathGeneradoGeneral = `files/${currentUser.uid}/${fileImagenGenerada.name}`;
    const storageRefGeneradoGeneral = ref(storage, storagePathGeneradoGeneral);

    try {
      await uploadBytes(storageRefGeneradoEspecifico, fileImagenGenerada);
      await uploadBytes(storageRefGeneradoGeneral, fileImagenGenerada);

      const filesCollection = collection(db, `users/${currentUser.uid}/models`);
      await addDoc(filesCollection, {
        filename: fileImagenGenerada.name,
      });
    } catch (error) {
      console.error("Error uploading file:", error);
    }

    // En móviles, cambia a `ImagenesSeleccionadas`
    if (isMobile) {
      setIsGenerada(true);
    }
  };
  /**************************************************************************************************/

  /*********************************** METODOS API COMFYUI ******************************************/
  //Ejecuta el workflow de ComfyUI y retorna una imagen
  const executeWorkflow = async (prenda, modelo) => {
    //1º Se suben la imagen de la prenda y del modelo
    await uploadImageComfy(prenda, "prenda");
    await uploadImageComfy(modelo, "modelo");

    //2º Se ejecuta el script de python que inicia el workflow de ComfyUI
    await runPythonScript();

    //3º Se cierra la conexión para evitar mantener conexiones abiertas
    closeSSHConnection();
  };

  //Método para subir una imagen a la carpeta de input a ComfyUI para que se pueda generar la imagen
  const uploadImageComfy = async (file, metodo) => {
    //Se traducen los datos del objeto File para poder pasarlos en una request HTTP
    const formData = new FormData();
    formData.append("image", file);

    try {
      //dependiendo de la request introducida, se sube la imagen con un nombre u otro
      const response = await fetch(`${SERVER_URL}/api/upload-image-` + metodo, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to upload image");

      //se recogen los datos devueltos por la request
      const data = await response.json();
      console.log("Image uploaded successfully:", data);
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  //Método para ejecutar el script de python
  const runPythonScript = async () => {
    //Se llama al método del servidor que se encarga de ejecutar el script de generación de imagen
    //en la máquina
    try {
      const response = await fetch(`${SERVER_URL}/api/run-python-script`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to execute Python script");
      } else {
        //Se espera a recibir la url de la imagen generada
        const data = await response.json();
        //se guarda la imagen y se actualiza la interfaz
        urlImagenGenerada = data.imageUrl;
        setImagenGenerada(data.imageUrl);
      }
    } catch (error) {
      console.error("Error running script:", error);
    }
  };

  //Método para cerrar la conexión ssh
  const closeSSHConnection = async () => {
    //Se llama al método del servidor que gestiona el cierre de la conexión ssh
    try {
      const response = await fetch(`${SERVER_URL}/api/close-connection`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      //Se recoge la info del proceso (mensajes informativos de error o ejecución)
      const data = await response.json();
      console.log(data.message);

      if (!response.ok) {
        throw new Error(data.error || "Failed to close connection");
      }
      console.log("SSH connection closed successfully!");
    } catch (error) {
      console.error("Error closing connection:", error);
      alert("Error closing SSH connection.");
    }
  };
  /**************************************************************************************************/

  //Convertir la URL de una imagen en un objeto tipo File
  async function urlToFile(url) {
    //se coge la ultima parte de la url que identifica a la imagen
    const fileName = url.substring(url.lastIndexOf("/") + 1);
    const response = await fetch(url);
    const blob = await response.blob();
    const file = new File([blob], fileName, { type: "image/png" });

    return file;
  }

  /***********************************************************************************************/
  /***********************************************************************************************/

  return (
    <div className="App">
      {/* Barra de navegación */}
      <Navbar />

      <main className="main-container">
        {/* Sidebar siempre visible */}
        <Sidebar />

        {/* Contenido principal adaptado a la pantalla */}
        <div className="main-content">
          {isMobile ? (
            // En móvil: cambia entre MenuPrincipal y ImagenesSeleccionadas
            isGenerada ? (
              <ImagenesSeleccionadas
                imagenSeleccionada={imagenSeleccionada}
                imagenGenerada={imagenGenerada}
                loading={loading}
              />
            ) : (
              <MenuPrincipal
                onMostrarModelos={() => setMostrarModal(true)}
                modeloSeleccionado={modeloSeleccionado}
                onImageUpload={setImagenSeleccionada}
                // Le pasamos el callback para que nos envíe la URL
                onImageGenerated={(url) => {
                  setImagenGenerada(url);
                  setIsGenerada(true);
                  setLoading(false); // apagar loader cuando ya tienes la imagen
                }}
                onLoadingChange={setLoading}
              />
            )
          ) : (
            // En escritorio: mostrar ambos, pero ocultar ImagenesSeleccionadas si no hay espacio
            <>
              <MenuPrincipal
                onMostrarModelos={() => setMostrarModal(true)}
                modeloSeleccionado={modeloSeleccionado}
                onImageUpload={setImagenSeleccionada}
                onImageGenerated={(url) => setImagenGenerada(url)}
                onLoadingChange={setLoading}
              />
              {!hideImagenes && (
                <ImagenesSeleccionadas
                  imagenSeleccionada={imagenSeleccionada}
                  loading={loading}
                  imagenGenerada={imagenGenerada}
                />
              )}
            </>
          )}
        </div>
      </main>

      {/* Modal de modelos */}
      <Modal
        show={mostrarModal}
        onHide={() => setMostrarModal(false)}
        onSeleccionarModelo={setModeloSeleccionado}
      />
    </div>
  );
}

export default Principal;
